import type { ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { IconInfoCircle } from '@tabler/icons-react';
import { cn } from 'helpers/cn';
import { METRIC_HELP, type MetricKey } from 'helpers/statistics-glossary';

/**
 * A figure's label with its explanation attached.
 *
 * One component for every metric on the page so the icon, its position and the
 * hit area are identical everywhere, and so a metric cannot end up explained in one
 * table and unexplained in another. Requires a TooltipProvider above it.
 */
type MetricLabelProps = {
  metric: MetricKey;
  /** Defaults to nothing so the caller keeps control of wording and casing. */
  children: ReactNode;
  className?: string;
  /** Extra sentence appended below the shared definition, for context this instance has. */
  detail?: string;
};

export const MetricLabel = ({ metric, children, className, detail }: MetricLabelProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      {/*
        The trigger wraps label and icon together rather than the icon alone: a 14px
        icon is an awkward hit target, and hovering the word is what people try.
      */}
      <span className={cn('inline-flex cursor-help items-center gap-1 whitespace-nowrap', className)}>
        {children}
        <IconInfoCircle className="text-dim size-3 shrink-0" aria-hidden />
      </span>
    </TooltipTrigger>
    <TooltipContent className="max-w-sm space-y-2">
      <p>{METRIC_HELP[metric]}</p>
      {detail && <p className="opacity-80">{detail}</p>}
    </TooltipContent>
  </Tooltip>
);
